(() => {
  const root = typeof window !== 'undefined' ? window : globalThis;

  function decodeHtml(value) {
    if (!value) {
      return '';
    }

    const textarea = root.document.createElement('textarea');
    textarea.innerHTML = String(value);
    return textarea.value;
  }

  function formatReviewDate(isoDate, locale) {
    if (!isoDate) {
      return '';
    }

    const date = new Date(isoDate);
    if (Number.isNaN(date.getTime())) {
      return '';
    }

    return new Intl.DateTimeFormat(locale || 'en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    }).format(date);
  }

  function normalizeReview(review, locale) {
    const displayName = review?.user?.display_name || review?.user?.displayName || 'Customer';
    const initial = displayName.trim().charAt(0).toUpperCase() || 'C';

    return {
      score: Number(review?.score) || 0,
      content: decodeHtml(review?.content || ''),
      name: displayName,
      initial,
      date: formatReviewDate(review?.created_at || review?.createdAt, locale),
    };
  }

  function buildDistribution(reviews) {
    const distribution = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };

    reviews.forEach((review) => {
      const score = Math.round(Number(review.score) || 0);
      if (distribution[score] !== undefined) {
        distribution[score] += 1;
      }
    });

    return distribution;
  }

  function computeAverageScore(reviews) {
    if (!reviews.length) {
      return 0;
    }

    const total = reviews.reduce((sum, review) => sum + (Number(review.score) || 0), 0);
    return total / reviews.length;
  }

  function parsePreloadedPayload(rawPayload) {
    if (!rawPayload) {
      return null;
    }

    try {
      const payload = typeof rawPayload === 'string' ? JSON.parse(rawPayload) : rawPayload;
      const bottomline = payload.bottomline || payload.bottomLine || {};
      const reviews = Array.isArray(payload.reviews) ? payload.reviews : [];
      const totalReviews = Number(
        bottomline.totalReview
        ?? bottomline.total_review
        ?? payload.pagination?.total
        ?? reviews.length
      ) || 0;
      const averageScore = Number(
        bottomline.averageScore
        ?? bottomline.average_score
        ?? computeAverageScore(reviews)
      ) || 0;
      const starDistribution = bottomline.starDistribution || bottomline.star_distribution || null;

      return {
        reviews,
        totalReviews,
        averageScore,
        starDistribution,
      };
    } catch (error) {
      return null;
    }
  }

  function registerProductYotpoReviews() {
    if (!root.document || !root.Alpine) {
      return;
    }

    root.Alpine.data('productYotpoReviews', (config = {}) => ({
      appKey: config.appKey || '',
      productId: config.productId || '',
      reviewsUrl: config.reviewsUrl || '',
      preloadSourceId: config.preloadSourceId || '',
      sourceId: config.sourceId || '',
      locale: config.locale || 'en-US',
      maxVisible: Number(config.maxVisible) || 0,
      reviewCountTemplate: config.reviewCountTemplate || '{{ count }} ratings',
      reviewCountOneTemplate: config.reviewCountOneTemplate || '',
      ratingLabelTemplate: config.ratingLabelTemplate || '{{ rating }} out of 5 stars',
      emptyLabel: config.emptyLabel || 'No reviews yet',
      panelOpen: true,
      loading: true,
      loadError: false,
      reviews: [],
      totalReviews: 0,
      averageScore: 0,
      distribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },

      init() {
        this.loadSourceData();
        const preloaded = this.readPreloadedData();
        if (preloaded) {
          this.applyPayload(preloaded);
        }

        this.fetchReviews();
      },

      loadSourceData() {
        if (!this.sourceId || !root.document) {
          return;
        }

        const source = root.document.getElementById(this.sourceId);
        if (!source) {
          return;
        }

        try {
          const data = JSON.parse(source.textContent || '{}');
          this.appKey = data.appKey || this.appKey;
          this.productId = data.productId || this.productId;
          this.reviewsUrl = data.reviewsUrl || this.reviewsUrl;
          this.preloadSourceId = data.preloadSourceId || this.preloadSourceId;
          this.locale = data.locale || this.locale;
          this.maxVisible = Number(data.maxVisible);
          if (!Number.isFinite(this.maxVisible) || this.maxVisible < 0) {
            this.maxVisible = 0;
          }
          this.reviewCountTemplate = data.reviewCountTemplate || this.reviewCountTemplate;
          this.reviewCountOneTemplate = data.reviewCountOneTemplate || this.reviewCountOneTemplate;
          this.ratingLabelTemplate = data.ratingLabelTemplate || this.ratingLabelTemplate;
          this.emptyLabel = data.emptyLabel || this.emptyLabel;
        } catch (error) {
          // Keep defaults when config JSON is unavailable.
        }
      },

      readPreloadedData() {
        if (!this.preloadSourceId || !root.document) {
          return null;
        }

        const source = root.document.getElementById(this.preloadSourceId);
        if (!source) {
          return null;
        }

        return parsePreloadedPayload(source.textContent || '');
      },

      applyPayload(payload, options = {}) {
        const locale = this.locale;
        const reviews = (payload.reviews || []).map((review) => normalizeReview(review, locale));
        this.reviews = reviews;
        this.totalReviews = Number(payload.totalReviews) || reviews.length;
        this.averageScore = Number(payload.averageScore) || computeAverageScore(reviews);

        if (payload.starDistribution) {
          this.distribution = {
            1: Number(payload.starDistribution['1']) || 0,
            2: Number(payload.starDistribution['2']) || 0,
            3: Number(payload.starDistribution['3']) || 0,
            4: Number(payload.starDistribution['4']) || 0,
            5: Number(payload.starDistribution['5']) || 0,
          };
        } else {
          this.distribution = buildDistribution(reviews);
        }

        if (options.publish !== false) {
          this.publishProductRating({ authoritative: Boolean(options.authoritative) });
        }
      },

      publishProductRating(options = {}) {
        const authoritative = Boolean(options.authoritative);
        const totalReviews = Number(this.totalReviews) || 0;

        if (!authoritative && totalReviews <= 0) {
          return;
        }

        const detail = {
          source: 'yotpo',
          rating: totalReviews > 0 ? this.averageScore : 0,
          ratingCount: totalReviews,
        };

        if (typeof this.$dispatch === 'function') {
          this.$dispatch('product-reviews:updated', detail);
          return;
        }

        if (!this.$el || typeof this.$el.dispatchEvent !== 'function') {
          return;
        }

        this.$el.dispatchEvent(new CustomEvent('product-reviews:updated', {
          bubbles: true,
          detail,
        }));
      },

      async fetchReviews() {
        if (!this.appKey || !this.productId) {
          this.loading = false;
          return;
        }

        try {
          const perPage = 50;
          let page = 1;
          let totalPages = 1;
          let totalReviews = 0;
          const allReviews = [];
          let starDistribution = null;
          let averageScore = 0;

          while (page <= totalPages) {
            const response = await fetch(
              `https://api.yotpo.com/products/${encodeURIComponent(this.appKey)}/${encodeURIComponent(this.productId)}/reviews.json?page=${page}&per_page=${perPage}`
            );

            if (!response.ok) {
              throw new Error(`Yotpo request failed: ${response.status}`);
            }

            const data = await response.json();
            const responseBody = data?.response || {};
            const pageReviews = Array.isArray(responseBody.reviews) ? responseBody.reviews : [];
            const pagination = responseBody.pagination || {};
            const bottomline = responseBody.bottomline || {};

            allReviews.push(...pageReviews);
            totalReviews = Number(
              responseBody.total_reviews
              ?? bottomline.total_review
              ?? bottomline.totalReview
              ?? pagination.total
              ?? allReviews.length
            ) || allReviews.length;
            averageScore = Number(
              bottomline.average_score
              ?? bottomline.averageScore
              ?? averageScore
            ) || averageScore;
            starDistribution = bottomline.star_distribution || bottomline.starDistribution || starDistribution;

            const pageSize = Number(pagination.per_page) || perPage;
            totalPages = Math.max(1, Math.ceil(totalReviews / pageSize));
            page += 1;

            if (!pageReviews.length) {
              break;
            }
          }

          this.applyPayload({
            reviews: allReviews,
            totalReviews,
            averageScore: averageScore || computeAverageScore(allReviews),
            starDistribution,
          }, { authoritative: true });
        } catch (error) {
          this.loadError = true;
        } finally {
          this.loading = false;
        }
      },

      get visibleReviews() {
        if (!this.maxVisible) {
          return this.reviews;
        }

        return this.reviews.slice(0, this.maxVisible);
      },

      get canLoopCarousel() {
        return this.visibleReviews.length > 1;
      },

      get loopReviews() {
        const reviews = this.visibleReviews;

        if (reviews.length <= 1) {
          return reviews.map((review, index) => ({
            ...review,
            loopKey: `review-${index}`,
          }));
        }

        return [
          ...reviews.map((review, index) => ({
            ...review,
            loopKey: `review-a-${index}`,
          })),
          ...reviews.map((review, index) => ({
            ...review,
            loopKey: `review-b-${index}`,
          })),
        ];
      },

      get hasReviews() {
        return this.totalReviews > 0 && this.reviews.length > 0;
      },

      get hasMoreReviews() {
        return this.reviews.length < this.totalReviews;
      },

      get averageScoreDisplay() {
        if (!this.hasReviews) {
          return '0.0';
        }

        return this.averageScore.toFixed(1);
      },

      get reviewCountLabel() {
        const template = Number(this.totalReviews) === 1 && this.reviewCountOneTemplate
          ? this.reviewCountOneTemplate
          : this.reviewCountTemplate;

        return template
          .replace('__COUNT__', String(this.totalReviews))
          .replace('{{ count }}', String(this.totalReviews));
      },

      get summaryRatingLabel() {
        return this.ratingLabelTemplate
          .replace('__RATING__', this.averageScoreDisplay)
          .replace('{{ rating }}', this.averageScoreDisplay);
      },

      get distributionRows() {
        const total = this.totalReviews > 0 ? this.totalReviews : 0;

        return [5, 4, 3, 2, 1].map((rating) => ({
          rating,
          percent: total > 0 ? Math.round((this.distribution[rating] / total) * 100) : 0,
        }));
      },

      reviewRatingLabel(score) {
        return this.ratingLabelTemplate
          .replace('__RATING__', String(score))
          .replace('{{ rating }}', String(score));
      },

      getCarouselLoopPoint() {
        const track = this.$refs.reviewTrack;

        if (!track || !this.canLoopCarousel) {
          return 0;
        }

        const slides = track.querySelectorAll('.product-page__client-review-slide');
        const originalCount = this.visibleReviews.length;

        if (slides.length < originalCount * 2) {
          return 0;
        }

        return slides[originalCount].offsetLeft;
      },

      normalizeCarouselLoop() {
        const track = this.$refs.reviewTrack;
        const loopPoint = this.getCarouselLoopPoint();

        if (!track || loopPoint <= 0) {
          return;
        }

        if (track.scrollLeft >= loopPoint - 2) {
          track.scrollLeft = track.scrollLeft - loopPoint;
        }
      },

      scrollCarouselNext() {
        const track = this.$refs.reviewTrack;

        if (!track) {
          return;
        }

        const slides = Array.from(track.querySelectorAll('.product-page__client-review-slide'));

        if (slides.length === 0) {
          return;
        }

        this.normalizeCarouselLoop();

        const scrollLeft = track.scrollLeft;
        const nextSlide = slides.find((slide) => slide.offsetLeft > scrollLeft + 4);

        if (!nextSlide) {
          return;
        }

        track.scrollTo({
          left: nextSlide.offsetLeft,
          behavior: 'smooth',
        });

        const settleLoop = () => {
          this.normalizeCarouselLoop();
        };

        if (typeof track.addEventListener === 'function') {
          track.addEventListener('scrollend', settleLoop, { once: true });
        }

        root.setTimeout(settleLoop, 450);
      },
    }));
  }

  if (root.document) {
    root.document.addEventListener('alpine:init', registerProductYotpoReviews);

    if (root.Alpine) {
      registerProductYotpoReviews();
    }
  }
})();
